import * as ex from 'excalibur';
import { TILE_SIZE } from '../config/constants';
import { Entity } from '../core/entity';
import { Animal } from '../core/animal';

/**
 * Manages particle effects throughout the game
 */
export class ParticleManager {
  private static instance: ParticleManager;
  private engine: ex.Engine | null = null;

  // Weather emitters
  private rainEmitter: ex.ParticleEmitter | null = null;
  private snowEmitter: ex.ParticleEmitter | null = null;

  // Weather state
  private _weatherActive: boolean = false;
  private _weatherType: 'rain' | 'snow' | 'none' = 'none';
  private _weatherIntensity: number = 0.5; // 0.0 to 1.0

  // Dust pool for reuse
  private dustEmitterPool: ex.ParticleEmitter[] = [];
  private activeDustEmitters: Map<ex.ParticleEmitter, number> = new Map(); // emitter -> expiration time
  private readonly DUST_POOL_SIZE = 20; // Maximum number of dust emitters to keep in pool
  private dustEmittersCreated = 0; // Counter for debugging

  // Private constructor for singleton pattern
  private constructor() { }

  /**
   * Get the singleton instance
   */
  public static getInstance(): ParticleManager {
    if (!ParticleManager.instance) {
      ParticleManager.instance = new ParticleManager();
    }
    return ParticleManager.instance;
  }

  /**
   * Initialize the particle manager with a reference to the engine
   */
  public initialize(engine: ex.Engine): void {
    this.engine = engine;
  }

  /**
   * Creates a dust effect when an animal hops
   * @param entity The entity that's moving
   * @param startX The starting X coordinate (world position)
   * @param startY The starting Y coordinate (world position)
   */
  public createDustEffect(entity: Entity, startX: number, startY: number): void {
    if (!this.engine || !(entity instanceof Animal)) return;

    try {
      // Try to get an emitter from the pool
      let emitter = this.dustEmitterPool.pop();

      // If no emitter in pool, create a new one (but limit total created)
      if (!emitter) {
        this.dustEmittersCreated++;
        console.log(`Creating new dust emitter #${this.dustEmittersCreated}`);

        // Create a direct emitter (not using nested particle property)
        emitter = new ex.ParticleEmitter({
          // Basic emitter properties
          x: 0,
          y: 0,
          width: 1,
          height: 1,

          // Emit in a half-circle upward
          emitterType: ex.EmitterType.Circle,
          radius: 2,
          minAngle: Math.PI / 2,    // 90 degrees (up)
          maxAngle: Math.PI * 3 / 2,  // 270 degrees (range covers up-left to up-right)

          // Emission properties
          isEmitting: false,
          emitRate: 200,            // Higher rate for short burst
          opacity: 1.0,             // Full opacity
          particleLife: 500,        // Shorter life for more pixel-like feel
          maxParticles: 15,

          // Particle properties - not using nested particle:{} for better compatibility
          beginColor: ex.Color.fromRGB(230, 220, 180), // Sandy color
          endColor: ex.Color.fromRGB(215, 205, 165),   // Slight fade to create depth
          minSize: 1,               // Smaller size for pixel-art feel
          maxSize: 1,               // Fixed size for consistent pixelated look
          minVel: 40,               // Faster for shorter life
          maxVel: 60,
          acceleration: new ex.Vector(0, -20),
          fadeFlag: true
        });

        // Add to engine/scene
        const scene = this.engine.currentScene;
        if (scene) {
          scene.add(emitter); // This is correct - direct adding to scene
        } else {
          this.engine.add(emitter);
        }
      }

      // Reset emitter state
      emitter.isEmitting = false;
      emitter.opacity = 1.0;

      // Position it correctly
      emitter.pos.x = startX;
      emitter.pos.y = startY + TILE_SIZE / 6;

      // Start emitting immediately
      emitter.isEmitting = true;

      // Track expiration time
      const expirationTime = Date.now() + 900; // Plenty of time for particles to finish
      this.activeDustEmitters.set(emitter, expirationTime);

      // Stop emission after a short burst
      setTimeout(() => {
        if (emitter) {
          emitter.isEmitting = false;
        }
      }, 150);

      // Debug info
      console.log(`Dust effect at (${startX}, ${startY}), pool size: ${this.dustEmitterPool.length}, active: ${this.activeDustEmitters.size}`);

    } catch (error) {
      console.error("Error creating dust effect:", error);
    }
  }

  /**
   * Update active particle emitters
   * Should be called each frame
   */
  public update(delta?: number): void {
    if (!delta) delta = 16; // Default to 16ms if no delta provided

    try {
      const now = Date.now();

      // Process dust emitters
      for (const [emitter, expirationTime] of this.activeDustEmitters.entries()) {
        try {
          // Force an update on the emitter with the current delta time
          emitter.update(delta);

          // Check if this emitter has expired
          if (now >= expirationTime) {
            // Return to pool if we're under the pool size limit
            if (this.dustEmitterPool.length < this.DUST_POOL_SIZE) {
              // Reset emitter state before returning to pool
              emitter.isEmitting = false;
              this.dustEmitterPool.push(emitter);
              console.log(`Returning dust emitter to pool, now ${this.dustEmitterPool.length} in pool`);
            } else {
              // Remove from engine/scene if pool is full
              const scene = this.engine?.currentScene;
              if (scene) {
                scene.remove(emitter);
              } else if (this.engine) {
                this.engine.remove(emitter);
              }
              console.log("Pool full, removing emitter from scene");
            }

            // Remove from active map
            this.activeDustEmitters.delete(emitter);
          }
        } catch (emitterError) {
          console.error("Error updating individual dust emitter:", emitterError);
          // Clean up problematic emitter
          this.activeDustEmitters.delete(emitter);
        }
      }

      // Update weather emitters if they exist
      if (this.rainEmitter) {
        this.rainEmitter.update(delta);
      }

      if (this.snowEmitter) {
        this.snowEmitter.update(delta);
      }
    } catch (error) {
      console.error("Error in particle manager update:", error);
    }
  }

  /**
   * Start rain effect with manual particle implementation
   */
  public startRain(intensity: number = 0.5): void {
    if (!this.engine) return;
    
    // Stop existing weather
    this.stopWeather();
    
    try {
      const scene = this.engine.currentScene;
      if (!scene) return;
      
      console.log("Creating manual rain drops");
      
      // Create a container actor
      const rainContainer = new ex.Actor({
        x: 0,
        y: 0,
        width: 1,
        height: 1
      });
      
      // Fixed position for screen space
      rainContainer.fixed = true;
      
      // Create initial rain drops across the screen (more for better effect)
      for (let i = 0; i < 80; i++) {
        // Randomly distribute initial raindrops throughout the screen
        const raindrop = new ex.Actor({
          x: Math.random() * 640,
          y: Math.random() * 480, // Distribute across screen height
          width: 1,
          height: 3,
          color: ex.Color.fromRGB(120, 170, 230)
        });
        
        raindrop.fixed = true;
        raindrop.vel.x = 5 + Math.random() * 15;
        raindrop.vel.y = 250 + Math.random() * 150;
        
        raindrop.on('postupdate', () => {
          if (raindrop.pos.y > 500) {
            scene.remove(raindrop);
          }
        });
        
        scene.add(raindrop);
      }
      
      // Create a timer to add more raindrops periodically
      const timer = new ex.Timer({
        fcn: () => {
          // Create multiple drops per interval for more density
          for (let i = 0; i < 3; i++) {
            createRaindrop(scene);
          }
        },
        interval: 50, // Faster interval
        repeats: true
      });
      scene.add(timer);
      timer.start();
      
      // Store reference to timer for cleanup
      this.rainEmitter = timer;
      
      // Function to create individual raindrops
      function createRaindrop(scene: ex.Scene) {
        // Create a raindrop (tiny blue pixel)
        const raindrop = new ex.Actor({
          x: Math.random() * 640, // Random x position
          y: -5, // Start above screen
          width: 1,  // Single pixel width for pixel art style
          height: 3, // Small vertical line 
          color: ex.Color.fromRGB(120, 170, 230) // Lighter blue for better visibility
        });
        
        // Screen space position
        raindrop.fixed = true;
        
        // Fast downward movement
        raindrop.vel.x = 5 + Math.random() * 15; // Slight angle
        raindrop.vel.y = 250 + Math.random() * 150; // Fast fall
        
        // Auto-remove when off screen
        raindrop.on('postupdate', () => {
          if (raindrop.pos.y > 500) {
            scene.remove(raindrop);
          }
        });
        
        // Add to scene
        scene.add(raindrop);
      }
      
      // State tracking
      this._weatherType = 'rain';
      this._weatherIntensity = intensity;
      this._weatherActive = true;
      
    } catch (error) {
      console.error("Error creating rain effect:", error);
    }
  }

  /**
   * Start snow effect with manual particle implementation
   */
  public startSnow(intensity: number = 0.5): void {
    if (!this.engine) return;
    
    // Stop existing weather
    this.stopWeather();
    
    try {
      const scene = this.engine.currentScene;
      if (!scene) return;
      
      console.log("Creating manual snowflakes");
      
      // Create initial snowflakes across the screen (more for better effect)
      for (let i = 0; i < 100; i++) {
        // Randomly distribute initial snowflakes throughout the screen
        const snowflake = new ex.Actor({
          x: Math.random() * 640,
          y: Math.random() * 480, // Distribute across screen height
          width: 1,
          height: 1,
          color: ex.Color.fromRGB(240, 240, 255)
        });
        
        snowflake.fixed = true;
        snowflake.vel.x = Math.sin(Date.now() * 0.001 + Math.random() * 10) * 15;
        snowflake.vel.y = 30 + Math.random() * 40;
        
        snowflake.on('postupdate', () => {
          snowflake.vel.x = Math.sin(Date.now() * 0.001 + Math.random()) * 15;
          if (snowflake.pos.y > 500) {
            scene.remove(snowflake);
          }
        });
        
        scene.add(snowflake);
      }
      
      // Create a timer to add more snowflakes periodically
      const timer = new ex.Timer({
        fcn: () => {
          // Create multiple snowflakes per interval for more density
          for (let i = 0; i < 2; i++) {
            createSnowflake(scene);
          }
        },
        interval: 100, // Faster interval
        repeats: true
      });
      scene.add(timer);
      timer.start();
      
      // Store reference to timer for cleanup
      this.snowEmitter = timer;
      
      // Function to create individual snowflakes
      function createSnowflake(scene: ex.Scene) {
        // Create pixel-art style snowflake (tiny square)
        const snowflake = new ex.Actor({
          x: Math.random() * 640, // Random x position
          y: -5, // Start above screen
          width: 1,  // Single pixel for pixel art style
          height: 1, // Square pixel
          color: ex.Color.fromRGB(240, 240, 255) // Slightly off-white for better blending
        });
        
        // Screen space position
        snowflake.fixed = true;
        
        // Gentle falling motion
        snowflake.vel.x = Math.sin(Date.now() * 0.001 + Math.random() * 10) * 15; // Wavy horizontal
        snowflake.vel.y = 30 + Math.random() * 40; // Slow fall
        
        // Auto-remove when off screen
        snowflake.on('postupdate', () => {
          // Slightly update horizontal velocity for waviness
          snowflake.vel.x = Math.sin(Date.now() * 0.001 + Math.random()) * 15;
          
          // Remove when off screen
          if (snowflake.pos.y > 500) {
            scene.remove(snowflake);
          }
        });
        
        // Add to scene
        scene.add(snowflake);
      }
      
      // State tracking
      this._weatherType = 'snow';
      this._weatherIntensity = intensity;
      this._weatherActive = true;
      
    } catch (error) {
      console.error("Error creating snow effect:", error);
    }
  }
  
  /**
   * Stop current weather effect
   */
  public stopWeather(): void {
    try {
      // Get current scene
      const scene = this.engine?.currentScene;
      if (!scene) return;
      
      // Find and remove all weather elements (by finding fixed actors with certain properties)
      const actorsToRemove: ex.Actor[] = [];
      
      // Find all actors in the scene
      scene.actors.forEach(actor => {
        // Check if it's a fixed position actor (our weather particles are fixed)
        if (actor.fixed) {
          // Raindrop check - blue, tiny height=3 rectangles 
          if (actor.height === 3 && actor.width === 1) {
            actorsToRemove.push(actor);
          }
          // Snowflake check - white, 1x1 squares
          else if (actor.height === 1 && actor.width === 1) {
            actorsToRemove.push(actor);
          }
        }
      });
      
      // Remove all identified weather particles
      for (const actor of actorsToRemove) {
        scene.remove(actor);
      }
      
      console.log(`Removed ${actorsToRemove.length} weather particles`);
      
      // Stop rain timer
      if (this.rainEmitter) {
        if (scene) {
          scene.remove(this.rainEmitter);
        }
        
        this.rainEmitter = null;
        console.log("Rain effect stopped");
      }
      
      // Stop snow timer
      if (this.snowEmitter) {
        if (scene) {
          scene.remove(this.snowEmitter);
        }
        
        this.snowEmitter = null;
        console.log("Snow effect stopped");
      }
      
      // Reset state
      this._weatherActive = false;
      this._weatherType = 'none';
      
    } catch (error) {
      console.error("Error stopping weather effects:", error);
    }
  }
  
  /**
   * Check if weather is active
   */
  public get weatherActive(): boolean {
    return this._weatherActive;
  }
  
  /**
   * Get current weather type
   */
  public get weatherType(): 'rain' | 'snow' | 'none' {
    return this._weatherType;
  }
  
  /**
   * Get current weather intensity
   */
  public get weatherIntensity(): number {
    return this._weatherIntensity;
  }
  
  /**
   * Test method for debugging purposes
   * (No longer creates a visible red circle)
   */
  public testVisibility(): void {
    if (!this.engine) {
      console.error("Engine not available for test");
      return;
    }
    
    try {
      const scene = this.engine.currentScene;
      if (!scene) {
        console.error("No scene available");
        return;
      }
      
      // Just log scene info for debugging
      console.log(`Scene active with ${scene.actors.length} actors`);
    } catch (error) {
      console.error("Error in test visibility:", error);
    }
  }
}